import { useCallback } from 'react';
import { createWithEqualityFn } from 'zustand/traditional';
import { shallow } from 'zustand/shallow';

interface DraftValueSnapshot {
  hasDraft: boolean;
  value: unknown;
}

interface DraftValueState {
  values: Record<string, Record<string, unknown>>;
  setValue: (reportId: string, keypath: string, value: unknown) => void;
  clearReport: (reportId: string) => void;
  clearAll: () => void;
}

const useReportDraftStore = createWithEqualityFn<DraftValueState>((set) => ({
  values: {},
  setValue: (reportId, keypath, value) =>
    set((state) => {
      const reportValues = state.values[reportId] ?? {};
      if (
        Object.prototype.hasOwnProperty.call(reportValues, keypath) &&
        Object.is(reportValues[keypath], value)
      ) {
        return state;
      }

      return {
        values: {
          ...state.values,
          [reportId]: {
            ...reportValues,
            [keypath]: value,
          },
        },
      };
    }),
  clearReport: (reportId) =>
    set((state) => {
      if (!state.values[reportId]) {
        return state;
      }
      const next = { ...state.values };
      delete next[reportId];
      return { values: next };
    }),
  clearAll: () => set({ values: {} }),
}));

export function useReportDraftValue(
  reportId: string | undefined,
  keypath: string | undefined
): DraftValueSnapshot {
  const selector = useCallback(
    (state: DraftValueState): DraftValueSnapshot => {
      if (!reportId || !keypath) {
        return { hasDraft: false, value: undefined };
      }

      const reportValues = state.values[reportId];
      if (!reportValues) {
        return { hasDraft: false, value: undefined };
      }

      if (!Object.prototype.hasOwnProperty.call(reportValues, keypath)) {
        return { hasDraft: false, value: undefined };
      }

      return { hasDraft: true, value: reportValues[keypath] };
    },
    [reportId, keypath]
  );

  return useReportDraftStore(selector, shallow);
}

export function setReportDraftValue(
  reportId: string | undefined,
  keypath: string | undefined,
  value: unknown
) {
  if (!reportId || !keypath) return;
  useReportDraftStore.getState().setValue(reportId, keypath, value);
}

export function clearReportDraft(reportId: string | undefined) {
  if (!reportId) return;
  useReportDraftStore.getState().clearReport(reportId);
}
